
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import vModal from "@/components/vModal.vue";
import AllergenMLRepository from "@/utils/API/AllergenMLRepository";
import { MlAnalysisResponse } from "@/models/AllergenML/AllergenML";
import { NutrientSource } from "@/models/NutrientSource/NutrientSource";
import AllergenOverrideMatrix from "@/components/allergens/AllergenOverrideMatrix.vue";

@Component({
  components: {
    vModal,
    AllergenOverrideMatrix,
  }
})
export default class VAllergenMLModal extends Vue {
  @Prop({ required: true }) show!: boolean;
  @Prop({ required: true }) targetType!: "ingredient" | "recipe" | "combined-recipe";
  @Prop({ required: true }) targetId!: number;
  @Prop({ required: true }) restaurantId!: number;
  @Prop({ default: "" }) targetName!: string;
  @Prop({ default: NutrientSource.US }) country!: NutrientSource;
  @Prop({ default: null }) initialAnalysisResult!: MlAnalysisResponse | null;
  @Prop({ default: true }) autoRunOnShow!: boolean;

  loading = false;
  errorMessage = "";
  analysisResult: MlAnalysisResponse | null = null;
  selectedCountry: NutrientSource = NutrientSource.US;
  countryOptions = [
    { label: "Canada", value: NutrientSource.Canada },
    { label: "USA", value: NutrientSource.US },
    { label: "UK", value: NutrientSource.UK },
  ];

  get modalTitle() {
    return "AI Allergen Detector";
  }

  get selectedCountryLabel() {
    return this.countryOptions.find((option) => option.value === this.selectedCountry)?.label || "USA";
  }

  get showCountrySelection() {
    return this.targetType !== "ingredient";
  }

  get modalMascotStateClass() {
    if (this.loading) {
      return "is-analyzing";
    }

    if (this.analysisResult) {
      return "is-ready";
    }

    return "is-idle";
  }

  get modalMascotKicker() {
    if (this.loading) {
      return "Analyzing";
    }

    if (this.analysisResult) {
      return "Results Ready";
    }

    return "Ready To Analyze";
  }

  get modalMascotSubtitle() {
    if (!this.showCountrySelection) {
      if (this.loading) {
        return "MenuSano AI is checking this custom ingredient for any allergen signals.";
      }

      if (this.analysisResult) {
        return "Review detected allergens, make any needed changes, and update the ingredient switches.";
      }

      return "Run the detector to review allergen detections for this custom ingredient and prepare the result for your switches.";
    }

    if (this.loading) {
      return `MenuSano AI is checking ingredients, allergen signals, and ${this.selectedCountryLabel} label rules.`;
    }

    if (this.analysisResult) {
      return "Review detected allergens, make any needed changes, and update the recipe switches.";
    }

    return `Run the detector to review allergen detections for ${this.selectedCountryLabel} and prepare the result for your switches.`;
  }

  get canAnalyze() {
    return this.targetId > 0 && this.restaurantId > 0;
  }

  get isCountryLocked() {
    return this.targetType === "recipe" || this.targetType === "combined-recipe";
  }

  get allergenAiPageHref() {
    if (!this.targetId) {
      return "";
    }

    const tabByTargetType = {
      ingredient: "customRecipes",
      recipe: "recipes",
      "combined-recipe": "combinedRecipes"
    } as const;

    return this.$router.resolve({
      name: "allergensAi",
      query: {
        tab: tabByTargetType[this.targetType],
        id: String(this.targetId),
        name: this.targetName || "",
        analyzed: this.analysisResult ? "1" : "0"
      }
    }).href;
  }

  created() {
    this.selectedCountry = this.country;
  }

  @Watch("show")
  async onShowChanged(value: boolean) {
    if (!value) {
      return;
    }

    this.errorMessage = "";

    if (this.initialAnalysisResult) {
      this.analysisResult = this.cloneAnalysisResult(this.initialAnalysisResult);
      return;
    }

    if (this.autoRunOnShow && this.canAnalyze) {
      await this.runAnalysis();
    }
  }

  @Watch("initialAnalysisResult")
  onInitialAnalysisResultChanged(value: MlAnalysisResponse | null) {
    if (!this.show) {
      return;
    }

    this.analysisResult = value ? this.cloneAnalysisResult(value) : null;
  }

  @Watch("country")
  onCountryPropChanged(value: NutrientSource) {
    this.selectedCountry = value;
  }

  @Watch("targetId")
  async clearResult() {
    this.analysisResult = this.initialAnalysisResult
      ? this.cloneAnalysisResult(this.initialAnalysisResult)
      : null;
    this.errorMessage = "";

    if (this.show && !this.initialAnalysisResult && this.autoRunOnShow && this.canAnalyze) {
      await this.runAnalysis();
    }
  }

  cloneAnalysisResult(result: MlAnalysisResponse) {
    return JSON.parse(JSON.stringify(result)) as MlAnalysisResponse;
  }

  async runAnalysis() {
    if (!this.canAnalyze) {
      this.errorMessage = "A saved item and restaurant context are required before running the analysis.";
      return;
    }
    this.loading = true;
    this.errorMessage = "";

    try {
      const response = this.targetType === "ingredient"
        ? await AllergenMLRepository.analyzeIngredient({
          restaurantId: this.restaurantId,
          ingredientId: this.targetId,
          countryId: this.showCountrySelection ? this.selectedCountry : undefined,
          nutrientSource: this.showCountrySelection ? this.selectedCountry : undefined
        })
        : await AllergenMLRepository.analyzeRecipe({
          restaurantId: this.restaurantId,
          recipeId: this.targetId,
          isCombinedRecipe: this.targetType === "combined-recipe",
          countryId: this.selectedCountry,
          nutrientSource: this.selectedCountry
        });

      this.analysisResult = response.data;
      this.$emit("analysis-completed", this.analysisResult);
    }
    catch (error: any) {
      this.errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "The Allergen AI analysis could not be completed.";
    }
    finally {
      this.loading = false;
    }
  }

  async handleCountryChange(event: Event) {
    if (this.isCountryLocked) {
      this.selectedCountry = this.country;
      return;
    }

    const target = event.target as HTMLSelectElement | null;
    this.selectedCountry = Number(target?.value ?? this.country) as NutrientSource;
    this.analysisResult = null;
    this.errorMessage = "";

    if (this.show && this.canAnalyze) {
      await this.runAnalysis();
    }
  }

  applyAnalysisResult() {
    if (!this.analysisResult) {
      return;
    }

    this.$emit("apply", this.analysisResult);
    this.$emit("close");
  }
}
